import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from '../../../axios/axiosBaseQuery';
import { Invitation, GetInvitationsParams } from '../invitation.model';
import Util from '../../../util/Util';
import { Base64 } from '../../../util/Base64';

const InvitationsBaseURL = new Util().getWorkSpaceManagementServiceURL();

export const InvitationsAPI = createApi({
  reducerPath: 'invitations',
  tagTypes: ['Invitations'],
  baseQuery: axiosBaseQuery({ baseUrl: InvitationsBaseURL }),
  endpoints: (build) => ({
    getInvitations: build.query<Invitation[], GetInvitationsParams>({
      query: ({ state, workspaceId }) => {
        const params = new URLSearchParams();
        if (state) params.append('state', state);
        let url = `/${Base64.encode(workspaceId)}/register/request`;
        if (params.size > 0) {
          url = `/${Base64.encode(
            workspaceId
          )}/register/request?${params.toString()}`;
        }
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['Invitations'],
    }),

    getInvitationById: build.query<
      Invitation,
      { workspaceId: string; requestId: string }
    >({
      query: ({ workspaceId, requestId }) => ({
        url: `/${Base64.encode(workspaceId)}/register/request/${requestId}`,
        method: 'GET',
      }),
      providesTags: ['Invitations'],
    }),

    createInvitation: build.mutation<Invitation, Partial<Invitation>>({
      query: (invitation) => ({
        url: `/workspaces/${invitation.workspaceId}/invitations`,
        method: 'POST',
        data: invitation,
      }),
      invalidatesTags: ['Invitations'],
    }),

    approveInvitation: build.mutation<Invitation, Partial<Invitation>>({
      query: (invitation) => ({
        url: `/${Base64.encode(
          invitation?.workspaceId || ''
        )}/register/request/${invitation?.requestId}/approve`,
        method: 'PUT',
        data: invitation,
      }),
      invalidatesTags: ['Invitations'],
    }),
    acceptInvitation: build.mutation<Invitation, Partial<Invitation>>({
      query: (invitation) => ({
        url: `/${Base64.encode(
          invitation?.workspaceId || ''
        )}/register/request/${invitation?.requestId}/accept`,
        method: 'PUT',
        data: invitation,
      }),
      invalidatesTags: ['Invitations'],
    }),
    rejectInvitation: build.mutation<Invitation, Partial<Invitation>>({
      query: (invitation) => ({
        url: `/${Base64.encode(
          invitation?.workspaceId || ''
        )}/register/request/${invitation?.requestId}/reject`,
        method: 'PUT',
      }),
      invalidatesTags: ['Invitations'],
    }),
    getInvitationToRegisterById: build.query<
      Invitation,
      { workspaceId: string; requestId: string }
    >({
      query: ({ workspaceId, requestId }) => ({
        url: `/${Base64.encode(
          workspaceId
        )}/register/request/${requestId}/invitation`,
        method: 'GET',
      }),
      providesTags: ['Invitations'],
    }),
  }),
});

export const {
  useGetInvitationsQuery,
  useGetInvitationByIdQuery,
  useCreateInvitationMutation,
  useApproveInvitationMutation,
  useRejectInvitationMutation,
} = InvitationsAPI;
